/**
 * RescheduleBooking Page
 * Public rescheduling page for existing bookings
 * Route: /booking/:bookingId/reschedule
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, User, ArrowLeft, MapPin, CheckCircle, AlertTriangle } from 'lucide-react';
import { schedulingApi } from '@/lib/api/services/scheduling';
import { Calendar } from '@/components/scheduling/Calendar';
import { TimeSlots } from '@/components/scheduling/TimeSlots';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { ApiError } from '@/lib/api';

interface Booking {
  id: string;
  invitee_name: string;
  invitee_email: string;
  start_time: string;
  end_time: string;
  timezone: string;
  status: string;
  cancellation_reason?: string;
  cancelled_by?: string;
  event_type: {
    id: string;
    name: string;
    duration: number;
    description?: string;
    color?: string;
    slug?: string;
  };
}

interface TimeSlot {
  start_time: string;
  end_time: string;
  available: boolean;
}

export default function RescheduleBooking() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [booking, setBooking] = useState<Booking | null>(null);

  const goBackToBookingPage = () => {
    const slug = booking?.event_type?.slug;
    if (slug) {
      navigate(`/book/${slug}`);
    } else {
      navigate('/');
    }
  };
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | undefined>();
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'date' | 'time' | 'confirm'>('date');
  const [rescheduled, setRescheduled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load booking details
  useEffect(() => {
    const loadBooking = async () => {
      try {
        setLoading(true);
        const response = await schedulingApi.getBookingById(bookingId!);
        if (response.success && response.data) {
          setBooking(response.data);
        } else {
          throw ApiError.fromResult(response, 'Booking not found');
        }
      } catch (err) {
        setError(friendlyMessage(err, 'Failed to load booking'));
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      loadBooking();
    }
  }, [bookingId]);

  // Load available slots when date is selected
  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedDate || !booking?.event_type?.id) return;

      try {
        setLoadingSlots(true);
        // Format date as YYYY-MM-DD without timezone conversion
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        const response = await schedulingApi.getAvailableSlotsForDate(booking.event_type.id, dateStr);
        const currentStart = new Date(booking.start_time).getTime();
        const slots = (response.data || []).filter(
          (slot: TimeSlot) => new Date(slot.start_time).getTime() !== currentStart
        );
        setAvailableSlots(slots);
        setStep('time');
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load available times',
          variant: 'destructive',
        });
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadSlots();
  }, [selectedDate, booking]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedSlot(undefined);
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep('confirm');
  };

  const handleReschedule = async () => {
    if (!selectedSlot || !booking) return;

    try {
      setSubmitting(true);
      const response = await schedulingApi.rescheduleBooking(bookingId!, {
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
      });

      if (response.success) {
        setRescheduled(true);
        toast({
          title: 'Booking Rescheduled',
          description: 'Your booking has been successfully rescheduled. Confirmation emails have been sent.',
        });
      } else {
        throw ApiError.fromResult(response, 'Failed to reschedule booking');
      }
    } catch (err) {
      showApiError(err, { fallback: { title: 'Failed to reschedule booking' } });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('time');
      setSelectedSlot(undefined);
    } else if (step === 'time') {
      setStep('date');
      setSelectedDate(undefined);
      setAvailableSlots([]);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Booking not found
  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Booking Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The booking you're looking for could not be found.
            </p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (rescheduled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Booking Rescheduled!</h2>
            <p className="text-muted-foreground mb-4">
              Your booking has been successfully rescheduled. Confirmation emails have been sent to all participants.
            </p>
            <Button onClick={goBackToBookingPage}>Schedule Another</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const bookingTz = booking.timezone || 'UTC';
  const formatInTz = (date: Date, fmt: 'date' | 'time') => {
    if (fmt === 'date') {
      return date.toLocaleDateString('en-US', {
        timeZone: bookingTz,
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      });
    }
    return date.toLocaleTimeString('en-US', {
      timeZone: bookingTz, hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  // Cancelled booking
  if (booking.status === 'cancelled') {
    const currentStartDate = new Date(booking.start_time);
    const currentEndDate = new Date(booking.end_time);

    return (
      <div className="min-h-screen bg-muted/30 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Booking Cancelled
              </CardTitle>
              <CardDescription>
                This booking has been cancelled and cannot be rescheduled.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Booking Details */}
              <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-lg">{booking.event_type.name}</h3>
                
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{booking.invitee_name}</span>
                </div>
                
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarIcon className="h-4 w-4" />
                  <span>{formatInTz(currentStartDate, 'date')}</span>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>
                    {formatInTz(currentStartDate, 'time')} - {formatInTz(currentEndDate, 'time')} ({booking.timezone})
                  </span>
                </div>
              </div>

              {/* Cancellation Reason */}
              {booking.cancellation_reason && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Cancellation Details
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-red-700">Cancelled by: </span>
                      <span className="text-sm text-red-700">
                        {booking.cancelled_by === 'host' ? 'Host' : booking.invitee_name || 'Invitee'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-red-700">Reason: </span>
                      <span className="text-sm text-red-700">{booking.cancellation_reason}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4">
                <Button onClick={goBackToBookingPage} className="w-full">
                  Schedule Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentStartDate = new Date(booking.start_time);
  const currentEndDate = new Date(booking.end_time);

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: booking.event_type.color || '#8b5cf6' }}
            >
              <CalendarIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Reschedule {booking.event_type.name}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {booking.event_type.duration} minutes
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {booking.invitee_name}
                </span>
              </div>
            </div>
          </div>

          {booking.event_type.description && (
            <p className="text-muted-foreground">{booking.event_type.description}</p>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Date/Time Selection */}
          <div className="order-2 lg:order-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">
                  {step === 'date' && 'Select New Date'}
                  {step === 'time' && 'Select New Time'}
                  {step === 'confirm' && 'Confirm Reschedule'}
                </CardTitle>
                <CardDescription className="text-sm">
                  {step === 'date' && 'Choose a new date for your meeting'}
                  {step === 'time' && 'Pick an available time slot'}
                  {step === 'confirm' && 'Review your new booking details'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {step !== 'date' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                    className="mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}

                {step === 'date' && (
                  <Calendar
                    onDateSelect={handleDateSelect}
                    selectedDate={selectedDate}
                    minDate={new Date()}
                  />
                )}

                {step === 'time' && (
                  <TimeSlots
                    slots={availableSlots}
                    selectedSlot={selectedSlot}
                    onSlotSelect={handleSlotSelect}
                    loading={loadingSlots}
                    timezone={booking.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                  />
                )}

                {step === 'confirm' && (
                  <div className="space-y-6">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h3 className="font-semibold mb-3">Confirm Reschedule</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Your booking will be moved from the current time to the new time selected below.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Current Time</p>
                          <p className="text-sm line-through opacity-60">
                            {formatInTz(currentStartDate, 'date')} at{' '}
                            {formatInTz(currentStartDate, 'time')} - {formatInTz(currentEndDate, 'time')}
                          </p>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-green-600">New Time</p>
                          <p className="text-sm font-semibold">
                            {selectedDate && selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at{' '}
                            {selectedSlot && formatInTz(new Date(selectedSlot.start_time), 'time')} -{' '}
                            {selectedSlot && formatInTz(new Date(selectedSlot.end_time), 'time')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleReschedule}
                      disabled={submitting}
                      className="w-full"
                      size="lg"
                    >
                      {submitting ? 'Rescheduling...' : 'Confirm Reschedule'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Summary */}
          <div className="order-1 lg:order-2">
            <Card className="lg:sticky lg:top-8">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Current Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Event</p>
                  <p className="font-semibold">{booking.event_type.name}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Invitee</p>
                  <p className="font-semibold">{booking.invitee_name}</p>
                  <p className="text-sm text-muted-foreground">{booking.invitee_email}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Current Date & Time</p>
                  <p className="font-semibold text-sm sm:text-base">
                    {formatInTz(currentStartDate, 'date')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatInTz(currentStartDate, 'time')} - {formatInTz(currentEndDate, 'time')} ({booking.timezone})
                  </p>
                </div>

                {selectedDate && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-green-600 mb-1 font-medium">New Date</p>
                    <p className="font-semibold text-sm sm:text-base">
                      {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                )}

                {selectedSlot && (
                  <div>
                    <p className="text-sm text-green-600 mb-1 font-medium">New Time</p>
                    <p className="font-semibold">
                      {formatInTz(new Date(selectedSlot.start_time), 'time')} -{' '}
                      {formatInTz(new Date(selectedSlot.end_time), 'time')}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Duration</p>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <p className="font-semibold">{booking.event_type.duration} minutes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}